import { capitalize, cn } from "@repo/shared/utils";
import type { Address } from "@repo/types";
import { Input } from "@repo/ui/components/Base/Input";
import { SelectItem } from "@repo/ui/components/Base/Select";
import { SelectInput } from "@repo/ui/components/Base/SelectInput";
import type { FormikErrors } from "formik";
import { states } from "@repo/lib/constants/states";

export const AddressFormField = <T,>({
  address = {
    state: "PA",
    street: "",
    street2: "",
    city: "",
    country: "",
    type: "work",
    zip: "",
  },
  fieldName,
  setFieldValue,
  orientation = "vertical",
  labelStyle,
  singleType,
  disabled = false,
}: {
  address?: Address;
  fieldName: string;
  setFieldValue: (
    field: string,
    value: Address,
    shouldValidate?: boolean | undefined
  ) => Promise<void | FormikErrors<T>>;
  orientation?: "vertical" | "horizontal";
  labelStyle?: string;
  singleType?: boolean;
  disabled?: boolean;
}) => {
  return (
    <div
      className={cn(
        "flex flex-col flex-grow",
        orientation === "horizontal" && "gap-x-3",
        orientation === "vertical" && "gap-y-1.5"
      )}
    >
      {!singleType && (
        <SelectInput
          label="*Address Type"
          placeholder="Select an address type"
          value={address.type ?? "work"}
          onValueChange={(value) =>
            setFieldValue(fieldName, { ...address, type: value })
          }
          options={["home", "work", "other"].map((role) => (
            <SelectItem key={role} value={role}>
              {capitalize(role)}
            </SelectItem>
          ))}
          orientation={orientation}
          labelStyle={labelStyle}
          disabled={disabled}
        />
      )}
      <Input
        value={address.street}
        label="Street"
        placeholder="1234 Washington St."
        id="street"
        name="street"
        onChange={(event) =>
          setFieldValue(fieldName, { ...address, street: event.target.value })
        }
        orientation={orientation}
        labelStyle={labelStyle}
        disabled={disabled}
      />
      <Input
        value={address.street2}
        label="Apt / Suite"
        placeholder="Ste 3"
        id="street2"
        name="street2"
        onChange={(event) =>
          setFieldValue(fieldName, { ...address, street2: event.target.value })
        }
        orientation={orientation}
        labelStyle={labelStyle}
        disabled={disabled}
      />
      <Input
        value={address.city}
        label="City"
        placeholder="Warren"
        id="city"
        name="city"
        onChange={(event) =>
          setFieldValue(fieldName, { ...address, city: event.target.value })
        }
        orientation={orientation}
        labelStyle={labelStyle}
        disabled={disabled}
      />

      <SelectInput
        label="State"
        placeholder="Select State"
        value={address.state}
        onValueChange={(value) =>
          setFieldValue(fieldName, { ...address, state: value })
        }
        options={Object.entries(states).map(([key, value]) => (
          <SelectItem key={key} value={key}>
            {value}
          </SelectItem>
        ))}
        orientation={orientation}
        labelStyle={labelStyle}
        disabled={disabled}
      />

      <Input
        value={address.zip}
        label="Zip"
        placeholder="16300"
        id="zip"
        name="zip"
        onChange={(event) =>
          setFieldValue(fieldName, { ...address, zip: event.target.value })
        }
        orientation={orientation}
        labelStyle={labelStyle}
        disabled={disabled}
      />
      <Input
        value={address.country}
        label="Country"
        placeholder="USA"
        id="country"
        name="country"
        onChange={(event) =>
          setFieldValue(fieldName, { ...address, country: event.target.value })
        }
        orientation={orientation}
        labelStyle={labelStyle}
        disabled={disabled}
      />
    </div>
  );
};
